import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import PatchBurst from '@ajat/design-system/PatchBurst';
import BlurReveal from '@ajat/design-system/BlurReveal';
import Button from '@ajat/design-system/Button';
import { ComponentPage, DemoCard } from '../../shared';
import { HERO_BURST } from './contentDemoData';

export const meta = {
  component: PatchBurst,
  title: 'Patch Burst',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/PatchBurst',
  description:
    'A short authored scene of patches materialising around a block of text in the same grammar as Blur Reveal — the DS blur, lift and the Gentle spring on everything — each at its own point, swelling with a little elasticity, tilting, hanging for a beat and dematerialising away (428ms each, staggered). HeroBannerV3 schedules it backwards from the moment the text reads sharp, so no patch lingers after the headline. Pure decoration: absolute layer over the host, aria-hidden, no pointer events, no layout. Plays once on mount and unmounts itself; PNGs are decoded before it starts; prefers-reduced-motion renders nothing. Three motions — pop-flick, swing-shrink, lift-vanish — assigned per item in the config, never random. Used by HeroBannerV3 `burst` on the OVER/LL landing.',
  props: [
    ['items', 'array', '[]', '[{ id, image, x, y, size, rotate, delay, motion, mobile }] — x/y in % of the host, size = width px, rotate = resting tilt deg, delay ms from scene start, mobile = {x, y, size} | false'],
    ['delay', 'number', '0', 'Offset for the whole scene, ms (align with the text reveal)'],
    ['onDone', 'func', '—', 'Called once the layer has unmounted after the last patch'],
    ['className', 'string', "''", 'Additional CSS class on the layer'],
  ],
  usage: [
    {
      title: 'Over a revealing headline',
      code: `import PatchBurst from '@ajat/design-system/PatchBurst';

const BURST = [
  { id: 'a', image: patchA, x: 76, y: 12, size: 84, rotate: 8, delay: 0, motion: 'pop-flick', mobile: { x: 84, y: 8, size: 52 } },
  { id: 'b', image: patchB, x: 4, y: 52, size: 64, rotate: -12, delay: 340, motion: 'lift-vanish', mobile: false },
];

<div style={{ position: 'relative' }}>
  <PatchBurst items={BURST} delay={120} />
  <BlurReveal as="h1" className="typo-h1-editorial">Official uniform of</BlurReveal>
</div>`,
    },
  ],
};

export default function PatchBurstPage() {
  const [run, setRun] = useState(0);
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="The OVER/LL hero scene" hint="Replay remounts the layer — desktop shows 4 patches, below 1024px 3">
        <Button size="tertiary" variant="outline" iconLeft={<RotateCcw />} onClick={() => setRun((n) => n + 1)}>
          Replay
        </Button>
        <div key={run} style={{ position: 'relative', maxWidth: 640, margin: '0 auto', padding: 'var(--space-ui-large-l) 0' }}>
          <PatchBurst items={HERO_BURST} delay={120} />
          <BlurReveal as="h1" className="typo-h1-editorial" delay={80} style={{ margin: 0, position: 'relative', zIndex: 2, textTransform: 'uppercase' }}>
            Official uniform of{' '}
            <span style={{ color: 'var(--color-brand-primary-alternative)' }}>unofficial behavior</span>
          </BlurReveal>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
